/**
 * A constant that ought to come from a measurement, carrying where and when it was measured (F223).
 *
 * **Why a type rather than a comment.** `ESCALATE_BELOW = 2` sat in a file for a month looking exactly
 * like a decision somebody took. It *was* a decision, once, measured against a corpus with no
 * near-duplicates and no newsletters (F156) — and nothing about the number said so, so nobody re-read it
 * when the corpus stopped resembling the one it came from. Thomas, 2026-09-27: *a number in a file looks
 * exactly like a decision; a number that says it has never been measured cannot.*
 *
 * So the value and its provenance are one object. A threshold nobody has measured has `measuredOn: null`
 * and **shows as never measured wherever it is shown** — on Engine, in a report, in a log — rather than
 * being indistinguishable from one that was measured this morning.
 *
 * **The registry is derived from these, never written beside them.** A second list of thresholds is two
 * things that have to agree, which is the shape G53 exists for: it agrees today and diverges the first
 * time one is changed. Whatever displays these reads the objects themselves.
 */

export interface Measured<T> {
  readonly value: T;
  /**
   * What the number was last measured against — a run, a corpus, a date — or **null when nobody has
   * measured it**. Null is not a gap in the record; it is the record, and it is the most important thing
   * this type carries.
   */
  readonly measuredOn: string | null;
  /** What it decides, in one line, so a reader knows what moving it costs. */
  readonly decides: string;
}

/** A number somebody measured, and what they measured it against. */
export const measuredOn = <T>(value: T, on: string, decides: string): Measured<T> => ({
  value,
  measuredOn: on,
  decides,
});

/**
 * A number nobody has measured yet. Deliberately as easy to write as the other one: the moment guessing
 * is harder than measuring, people stop recording that they guessed.
 */
export const neverMeasured = <T>(value: T, decides: string): Measured<T> => ({
  value,
  measuredOn: null,
  decides,
});

/** How a threshold is shown, in one place, so *never measured* reads the same way everywhere. */
export const sourceOf = (m: Measured<unknown>): string => m.measuredOn ?? 'never measured';
